import React, { useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { Reviews, InlineTextButton, ShowReviewModal } from '../../components';
import { THUMB_TYPE_DISLIKE, THUMB_TYPE_LIKE } from '../../util/types';
import { notification } from '../../util/notification';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';

import css from './ReviewsPage.module.css';

const POINTS_MODAL_COOLDOWN_HOURS = 2;

const shouldShowPointsModal = () => {
  const lastShownKey = 'pointsModalLastShown';
  const lastShown = localStorage.getItem(lastShownKey);
  
  if (!lastShown) {
    return true;
  }
  
  const now = Date.now();
  const lastShownTime = parseInt(lastShown, 10);
  const hoursSinceLastShown = (now - lastShownTime) / (1000 * 60 * 60);
  
  return hoursSinceLastShown >= POINTS_MODAL_COOLDOWN_HOURS;
};

const recordPointsModalShown = () => {
  localStorage.setItem('pointsModalLastShown', Date.now().toString());
};

const redirectToLoginPage = (location, history) => {
  const state = {
    from: `${location.pathname}${location.search}${location.hash}`,
  };

  const routes = routeConfiguration();
  history.push(createResourceLocatorString('LoginPage', routes), state);
};

const ReviewsHeading = ({ reviews, addReviewButton }) => (
  <div className={css.reviewsHeadingContainer}>
    <h2 className={css.reviewsHeading}>
      <FormattedMessage id="ListingPage.reviewsHeading" values={{ count: reviews.length }} />
    </h2>
    {addReviewButton}
  </div>
);

const ReviewSection = props => {
  const {
    reviews,
    currentUserReactions,
    handleReviewLikeOrDislike,
    fetchReviewsError,
    addReviewLikeInProgress,
    addReviewDislikeInProgress,
    addReviewLikeError,
    addReviewDislikeError,
    addReviewButton,
    reviewModal,
  } = props;

  return (
    <div className={css.sectionReviews}>
      <ReviewsHeading reviews={reviews} addReviewButton={addReviewButton} />
      {fetchReviewsError ? (
        <h2 className={css.errorText}>
          <FormattedMessage id="ListingPage.reviewsError" />
        </h2>
      ) : null}
      <Reviews
        reviews={reviews}
        currentUserReactions={currentUserReactions}
        handleReviewLikeOrDislike={handleReviewLikeOrDislike}
        likeOrDislikeReviewInProgress={addReviewLikeInProgress || addReviewDislikeInProgress}
        likeOrDislikeReviewError={addReviewLikeError || addReviewDislikeError}
      />
      {reviewModal}
    </div>
  );
};

const SectionReviews = props => {
  const {
    location,
    history,
    currentUser,
    currentUserReactions,
    reviews,
    fetchReviewsError,
    listingTitle,
    isOwnListing,
    sendReviewInProgress,
    sendReviewError,
    addReviewLikeInProgress,
    addReviewLikeError,
    addReviewDislikeInProgress,
    addReviewDislikeError,
    onSendReview,
    onAddReviewLike,
    onAddReviewDislike,
    onOpenPointsModal,
    onManageDisableScrolling,
  } = props;

  const [isReviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  const handleReviewLikeOrDislike = (type, reviewId) => {
    if (currentUser) {
      if (type === THUMB_TYPE_LIKE) {
        onAddReviewLike(reviewId);
      } else if (type === THUMB_TYPE_DISLIKE) {
        onAddReviewDislike(reviewId);
      } else {
        console.error(new Error('Unknown like or dislike type'), {
          likeOrDislikeType: type,
        });
      }
    } else {
      redirectToLoginPage(location, history);
    }
  };

  const handleOpenReviewModal = () => {
    if (currentUser) {
      setReviewModalOpen(true);
    } else {
      redirectToLoginPage(location, history);
    }
  };

  const handleReviewSubmit = (values, form) => {
    onSendReview(values).then(() => {
      setReviewModalOpen(false);
      setReviewSubmitted(true);
      form.restart();
      notification.success(
        'Review submitted successfully. It will appear on the platform shortly.'
      );
      if (shouldShowPointsModal()) {
        onOpenPointsModal(10);
        recordPointsModalShown();
      }
    });
  };

  const addReviewButton = isOwnListing
    ? null
    : !reviewSubmitted && (
        <InlineTextButton
          type="button"
          className={css.reviewButton}
          onClick={handleOpenReviewModal}
        >
          {reviews.length > 0 ? (
            <FormattedMessage id="ListingPage.reviewButton" />
          ) : (
            <FormattedMessage id="ListingPage.noReviewsButton" />
          )}
        </InlineTextButton>
      );

  const reviewModal = (
    <ShowReviewModal
      id="ReviewOrderModal"
      isOpen={isReviewModalOpen}
      onCloseModal={() => setReviewModalOpen(false)}
      onManageDisableScrolling={onManageDisableScrolling}
      onSubmitReview={handleReviewSubmit}
      revieweeName={listingTitle}
      reviewSent={reviewSubmitted}
      sendReviewInProgress={sendReviewInProgress}
      sendReviewError={sendReviewError}
    />
  );

  return (
    <ReviewSection
      reviews={reviews}
      currentUserReactions={currentUserReactions}
      handleReviewLikeOrDislike={handleReviewLikeOrDislike}
      fetchReviewsError={fetchReviewsError}
      addReviewLikeInProgress={addReviewLikeInProgress}
      addReviewDislikeInProgress={addReviewDislikeInProgress}
      addReviewLikeError={addReviewLikeError}
      addReviewDislikeError={addReviewDislikeError}
      addReviewButton={addReviewButton}
      reviewModal={reviewModal}
    />
  );
};

export default SectionReviews;
